import { stringLiteral } from '@execonline-inc/decoders';
import { boolean, Decoder, date, field, number, oneOf, string, succeed } from 'jsonous';
import { nullableField, readonlyArray } from '../../../../../../Decoders';
import { resourceDecoder } from '../../../../../../Resource/Decoders';
import {
  AccessStarts,
  CollectionLicenseOption,
  CollectionPrograms,
  Coverage,
  GrantingLicense,
  GroupParticipant,
  LicenseAlertsResource,
  LicenseAvailability,
  LicenseGroup,
  ProgramFamily,
} from './Types';

const coverageDecoder: Decoder<Coverage> = oneOf<Coverage>([
  stringLiteral<Coverage>('full'),
  stringLiteral<Coverage>('partial'),
  stringLiteral<Coverage>('none'),
]);

const accessStartsDecoder: Decoder<AccessStarts> = oneOf<AccessStarts>([
  stringLiteral<AccessStarts>('now'),
  stringLiteral<AccessStarts>('later'),
  stringLiteral<AccessStarts>('partly-later'),
  stringLiteral<AccessStarts>('after-collection-end'),
]);

const licenseAvailabilityDecoder: Decoder<LicenseAvailability> = oneOf<LicenseAvailability>([
  stringLiteral<LicenseAvailability>('available'),
  stringLiteral<LicenseAvailability>('not-yet-started'),
  stringLiteral<LicenseAvailability>('expired'),
  stringLiteral<LicenseAvailability>('not-held'),
]);

const grantingLicenseDecoder: Decoder<GrantingLicense> = succeed({})
  .assign('name', field('name', string))
  .assign('status', field('status', licenseAvailabilityDecoder));

const programFamilyDecoder: Decoder<ProgramFamily> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('offeringType', field('offering_type', string))
  .assign('grantingLicenses', field('granting_licenses', readonlyArray(grantingLicenseDecoder)));

const groupParticipantDecoder: Decoder<GroupParticipant> = succeed({})
  .assign('id', field('id', number))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string))
  .assign('email', field('email', string))
  .assign('licenseLabel', nullableField('license_label', string))
  .assign('licenseStartOn', nullableField('license_start_on', date))
  .assign('licenseEndOn', nullableField('license_end_on', date));

const licenseGroupDecoder: Decoder<LicenseGroup> = succeed({})
  .assign('key', field('key', string))
  .assign('primaryLicenseLabel', nullableField('primary_license_label', string))
  .assign('coverage', field('coverage', coverageDecoder))
  .assign('hiddenProgramFamilyIds', field('hidden_program_family_ids', readonlyArray(number)))
  .assign('expiresBeforeCollectionEnd', field('expires_before_collection_end', boolean))
  .assign('expiresOn', nullableField('expires_on', date))
  .assign('accessStarts', field('access_starts', accessStartsDecoder))
  .assign('startsOn', nullableField('starts_on', date))
  .assign('participants', field('participants', readonlyArray(groupParticipantDecoder)));

const collectionLicenseOptionDecoder: Decoder<CollectionLicenseOption> = succeed({})
  .assign('name', field('name', string))
  .assign('status', field('status', licenseAvailabilityDecoder))
  .assign('courseCount', field('course_count', number));

const collectionProgramsDecoder: Decoder<CollectionPrograms> = succeed({})
  .assign('programFamilies', field('program_families', readonlyArray(programFamilyDecoder)))
  .assign('lastProgramEndOn', nullableField('last_program_end_on', date))
  .assign(
    'licenseOptions',
    field('license_options', readonlyArray(collectionLicenseOptionDecoder)),
  );

const licenseAlertsDecoder = succeed({})
  .assign('collection', field('collection', collectionProgramsDecoder))
  .assign('licenseGroups', field('license_groups', readonlyArray(licenseGroupDecoder)))
  .assign('notFound', field('not_found', readonlyArray(string)));

export const licenseAlertsResponseDecoder: Decoder<LicenseAlertsResource> =
  resourceDecoder(licenseAlertsDecoder);
